// TODO(brand): migrate to <Button>
import React, { useEffect, useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Organization, UserProfile, AppSettings, StatThresholds, Team,
  DEFAULT_STAT_THRESHOLDS, THRESHOLD_PRESETS, DEFAULT_INSIGHT_STATS,
  ROLE_LABELS, ASSIGNABLE_ROLES, COACH_ROLES,
  AdvancedStatToggles, DEFAULT_ADVANCED_STAT_TOGGLES
} from '../types';
import {
  getOrgMembers, updateMemberRole, updateMemberAdminStatus,
  removeMember, sendOrgInvite, approveRequest, rejectRequest, updateUserProfile,
  supabase, mapTeamFromDB
} from '../services/supabase';
import {
  Users, Copy, Check, Shield, Trash2, UserCog, Mail,
  QrCode, UserPlus, Settings as SettingsIcon, User, ShieldCheck,
  ShieldOff, Clock, RefreshCw, Ruler, Send, AlertTriangle, ChevronDown, X, BarChart2, Palette, Info, Sparkles, ArchiveRestore, Wind, UserX, Loader2
} from 'lucide-react';
import { INSIGHT_CATEGORIES } from './InsightStatSettings';
import DeleteAccountModal from './DeleteAccountModal';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Member {
  memberId: string;
  userId: string | null;
  role: string;
  isAdmin: boolean;
  status: 'active' | 'pending' | 'invited';
  email: string;
  fullName: string;
  requestedRole?: string;
}

type TabType = 'profile' | 'members' | 'access' | 'appSettings';

interface OrgSettingsProps {
  organization: Organization;
  currentUser: UserProfile;
  appSettings: AppSettings;
  setAppSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
  onUpdateUser?: (name: string) => void;
  onUpdateOrg?: (updates: Partial<Organization>) => void;
  initialTab?: TabType;
  onRestartTour?: () => void;
  setTeams?: React.Dispatch<React.SetStateAction<Team[]>>;
}

// ─── Role color helper ───────────────────────────────────────────────────────

const roleColor = (role: string) => {
  switch (role) {
    case 'head_coach': return 'bg-purple-100 text-purple-700';
    case 'assistant_coach': case 'assistant': return 'bg-blue-100 text-blue-700';
    case 'coach': return 'bg-indigo-100 text-indigo-700';
    case 'team_manager': return 'bg-cyan-100 text-cyan-700';
    case 'stat_keeper': return 'bg-teal-100 text-teal-700';
    case 'parent': return 'bg-green-100 text-green-700';
    default: return 'bg-slate-100 text-slate-600';
  }
};

// ─── Dev Mode mock members ───────────────────────────────────────────────────

const buildDevMembers = (currentUser: UserProfile): Member[] => [
  { memberId: '1', userId: currentUser.id, role: 'head_coach', isAdmin: true, status: 'active', email: currentUser.email, fullName: currentUser.fullName },
  { memberId: '2', userId: 'asst-1', role: 'assistant_coach', isAdmin: false, status: 'active', email: 'mike@example.com', fullName: 'Coach Mike' },
  { memberId: '3', userId: 'mgr-1', role: 'team_manager', isAdmin: false, status: 'active', email: 'sam@example.com', fullName: 'Sam Rivera' },
  { memberId: '4', userId: 'parent-1', role: 'parent', isAdmin: false, status: 'active', email: 'jones@example.com', fullName: 'Sarah Jones' },
  { memberId: '5', userId: null, role: 'other', isAdmin: false, status: 'pending', email: '', fullName: 'Alex Kim', requestedRole: 'head_coach' },
  { memberId: '6', userId: null, role: 'assistant_coach', isAdmin: false, status: 'invited', email: 'new@example.com', fullName: 'Taylor New' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

const OrgSettings: React.FC<OrgSettingsProps> = ({
  organization, currentUser, appSettings, setAppSettings, onUpdateUser, onUpdateOrg, initialTab, onRestartTour, setTeams
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(initialTab || 'members');
  const isAdmin = !!organization.isAdmin;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [archivedTeams, setArchivedTeams] = useState<Team[]>([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [restoringTeamId, setRestoringTeamId] = useState<string | null>(null);

  // ── AI usage ──
  interface FeatureUsage { feature: string; tokens: number }
  const [aiUsage, setAiUsage] = useState<FeatureUsage[]>([]);
  const [aiMonthlyLimit, setAiMonthlyLimit] = useState<number>(50000);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'appSettings' || organization.id === 'dev-org-id') return;
    setAiUsageLoading(true);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    Promise.all([
      supabase.from('ai_usage')
        .select('feature, input_tokens, output_tokens')
        .eq('org_id', organization.id)
        .gte('created_at', monthStart),
      supabase.from('ai_quotas')
        .select('monthly_token_limit')
        .eq('org_id', organization.id)
        .maybeSingle(),
    ]).then(([usageRes, quotaRes]) => {
      if (quotaRes.data?.monthly_token_limit) {
        setAiMonthlyLimit(quotaRes.data.monthly_token_limit);
      }
      if (usageRes.data) {
        const byFeature: Record<string, number> = {};
        for (const row of usageRes.data) {
          const key = row.feature ?? 'other';
          byFeature[key] = (byFeature[key] ?? 0) + (row.input_tokens ?? 0) + (row.output_tokens ?? 0);
        }
        setAiUsage(Object.entries(byFeature).map(([feature, tokens]) => ({ feature, tokens })).sort((a, b) => b.tokens - a.tokens));
      }
      setAiUsageLoading(false);
    }).catch(() => setAiUsageLoading(false));
  }, [activeTab, organization.id]);

  // ── Archived teams ──
  useEffect(() => {
    if (activeTab !== 'appSettings' || organization.id === 'dev-org-id') return;
    setLoadingArchived(true);
    supabase.from('teams').select('*').eq('organization_id', organization.id).eq('is_archived', true)
      .then(({ data }) => {
        setArchivedTeams(data ? data.map(mapTeamFromDB) : []);
        setLoadingArchived(false);
      });
  }, [activeTab, organization.id]);

  const handleRestoreTeam = async (team: Team) => {
    setRestoringTeamId(team.id);
    await supabase.from('teams').update({ is_archived: false }).eq('id', team.id);
    setArchivedTeams(prev => prev.filter(t => t.id !== team.id));
    if (setTeams) setTeams(prev => [...prev, { ...team, isArchived: false }]);
    setRestoringTeamId(null);
  };

  // ── Members state ──
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberError, setMemberError] = useState<string | null>(null);

  // ── Deletion request state ──
  const [deletionTarget, setDeletionTarget] = useState<Member | null>(null);
  const [deletionStep, setDeletionStep] = useState<1 | 2>(1);
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [deletionSuccessMsg, setDeletionSuccessMsg] = useState<string | null>(null);

  // ── Profile state ──
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(currentUser.fullName);
  const [savingName, setSavingName] = useState(false);

  // ── Invite form state ──
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('assistant_coach');
  const [inviteAdmin, setInviteAdmin] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // ── Join code copy ──
  const [copied, setCopied] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);

  // ── Stat thresholds panel ──
  const [showThresholds, setShowThresholds] = useState(false);
  const [showStatConfig, setShowStatConfig] = useState(false);

  const joinUrl = `${window.location.origin}/?join=${organization.joinCode}`;

  // ── Fetch members ──
  useEffect(() => {
    fetchMembers();
  }, [organization.id]);

  // Score a member by how far along they are — higher = better to keep when deduping
  const memberScore = (m: Member) => {
    const base = m.status === 'active' ? 30 : m.status === 'pending' ? 20 : 10;
    return base + (m.userId ? 5 : 0) + (m.isAdmin ? 1 : 0);
  };

  // Remove duplicate rows that share the same email, keeping the highest-scored one
  const dedupeMembers = async (data: Member[]): Promise<Member[]> => {
    const byEmail: Record<string, Member[]> = {};
    data.forEach(m => {
      const key = (m.email || '').toLowerCase().trim();
      if (!key) return;
      if (!byEmail[key]) byEmail[key] = [];
      byEmail[key].push(m);
    });

    const toDelete: string[] = [];
    for (const group of Object.values(byEmail)) {
      if (group.length < 2) continue;
      group.sort((a, b) => memberScore(b) - memberScore(a)); // best first
      group.slice(1).forEach(m => toDelete.push(m.memberId)); // delete the rest
    }

    if (toDelete.length > 0) {
      await Promise.all(toDelete.map(id => removeMember(id)));
      return data.filter(m => !toDelete.includes(m.memberId));
    }
    return data;
  };

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const raw = await getOrgMembers(organization.id);
      const data = organization.id !== 'dev-org-id' ? await dedupeMembers(raw) : raw;
      setMembers(data);
    } catch (e) {
      console.error('Error fetching members', e);
      if (organization.id === 'dev-org-id') {
        setMembers(buildDevMembers(currentUser));
      } else {
        setMemberError('Failed to load members.');
      }
    } finally {
      setLoadingMembers(false);
    }
  };

  // ── Counts ──
  const totalCount = members.length; // all members including self, all statuses
  const activeCount = members.filter(m => m.status === 'active').length;
  const pendingCount = members.filter(m => m.status === 'pending').length;
  const nonActiveCount = totalCount - activeCount; // pending + invited

  // ─── Profile actions ─────────────────────────────────────────────────────

  const handleSaveName = async () => {
    if (!nameValue.trim() || nameValue === currentUser.fullName) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      if (organization.id !== 'dev-org-id') {
        await updateUserProfile(currentUser.id, nameValue.trim());
      }
      onUpdateUser?.(nameValue.trim());
      setEditingName(false);
    } catch (e) {
      console.error('Error saving name', e);
    } finally {
      setSavingName(false);
    }
  };

  // ─── Member actions ──────────────────────────────────────────────────────

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      if (organization.id !== 'dev-org-id') await updateMemberRole(memberId, newRole);
      setMembers(prev => prev.map(m => m.memberId === memberId ? { ...m, role: newRole } : m));
      setMemberError(null);
    } catch (e: any) {
      console.error('Role update error:', e);
      setMemberError(`Failed to update role: ${e?.message || 'Unknown error'}`);
    }
  };

  const handleAdminToggle = async (memberId: string, makeAdmin: boolean) => {
    try {
      if (organization.id !== 'dev-org-id') await updateMemberAdminStatus(memberId, makeAdmin);
      setMembers(prev => prev.map(m => m.memberId === memberId ? { ...m, isAdmin: makeAdmin } : m));
    } catch (e) {
      setMemberError('Failed to update admin status.');
    }
  };

  const handleRemoveMember = async (memberId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from this organization?`)) return;
    try {
      if (organization.id !== 'dev-org-id') await removeMember(memberId);
      setMembers(prev => prev.filter(m => m.memberId !== memberId));
    } catch (e) {
      setMemberError('Failed to remove member.');
    }
  };

  const handleRequestDeletion = async () => {
    if (!deletionTarget?.userId) return;
    setRequestingDeletion(true);
    try {
      const res = await fetch('/api/admin/deletion-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
        body: JSON.stringify({ targetUserId: deletionTarget.userId, orgId: organization.id }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || 'Failed to create deletion request.');
      }
      setDeletionTarget(null);
      setDeletionStep(1);
      setDeletionSuccessMsg(`Deletion request sent to ${deletionTarget.email}. They have 48 hours to cancel.`);
      setTimeout(() => setDeletionSuccessMsg(null), 8000);
    } catch (e: any) {
      setMemberError(e?.message || 'Failed to send deletion request.');
    } finally {
      setRequestingDeletion(false);
    }
  };

  // ─── Pending request actions ─────────────────────────────────────────────

  const handleApprove = async (member: Member) => {
    const role = member.requestedRole || member.role;
    try {
      if (organization.id !== 'dev-org-id') await approveRequest(member.memberId, role, organization.id);
      setMembers(prev => prev.map(m =>
        m.memberId === member.memberId ? { ...m, role, status: 'active', requestedRole: undefined } : m
      ));
      // Notify super-admin of new active member (best-effort)
      fetch('/api/beta/notify-member-joined', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgName: organization.name, memberName: member.fullName, memberEmail: member.email, role }),
      }).then(r => { if (!r.ok) r.json().then(d => console.warn('[beta]', r.url.split('/').pop(), d?.error)).catch(() => {}); }).catch(e => console.warn('[beta] fetch error:', e));
      // Check membership milestones (best-effort)
      fetch('/api/beta/notify-member-milestone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId: organization.id, orgName: organization.name }),
      }).then(r => { if (!r.ok) r.json().then(d => console.warn('[beta]', r.url.split('/').pop(), d?.error)).catch(() => {}); }).catch(e => console.warn('[beta] fetch error:', e));
    } catch (e: any) {
      setMemberError(e?.message || 'Failed to approve request.');
    }
  };

  const handleReject = async (member: Member) => {
    if (!window.confirm(`Reject ${member.fullName || 'this user'}'s request to join?`)) return;
    try {
      if (organization.id !== 'dev-org-id') await rejectRequest(member.memberId);
      setMembers(prev => prev.filter(m => m.memberId !== member.memberId));
    } catch (e) {
      setMemberError('Failed to reject request.');
    }
  };

  // ─── Invite actions ──────────────────────────────────────────────────────

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteName.trim() || !inviteEmail.trim()) return;
    setSendingInvite(true);
    setInviteError(null);
    try {
      if (organization.id !== 'dev-org-id') {
        await sendOrgInvite(organization.id, inviteName.trim(), inviteEmail.trim(), inviteRole, inviteAdmin);
      }
      // Add to local state
      const newInvite: Member = {
        memberId: `inv-${Date.now()}`,
        userId: null,
        role: inviteRole,
        isAdmin: inviteAdmin,
        status: 'invited',
        email: inviteEmail.trim(),
        fullName: inviteName.trim(),
      };
      setMembers(prev => [...prev, newInvite]);
      setInviteName('');
      setInviteEmail('');
      setInviteRole('assistant_coach');
      setInviteAdmin(false);
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 4000);
      // Check membership milestones (best-effort)
      fetch('/api/beta/notify-member-milestone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId: organization.id, orgName: organization.name }),
      }).then(r => { if (!r.ok) r.json().then(d => console.warn('[beta]', r.url.split('/').pop(), d?.error)).catch(() => {}); }).catch(e => console.warn('[beta] fetch error:', e));
    } catch (e: any) {
      console.error('Invite error:', e);
      setInviteError(e?.message || 'Failed to create invite.');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(organization.joinCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyJoinUrl = () => {
    navigator.clipboard.writeText(joinUrl);
    setQrCopied(true);
    setTimeout(() => setQrCopied(false), 2000);
  };

  // ─── App Settings helpers ────────────────────────────────────────────────

  const handleSettingChange = (key: keyof AppSettings, value: any) => {
    setAppSettings(prev => ({ ...prev, [key]: value }));
  };

  // ─── Tab nav ─────────────────────────────────────────────────────────────

  const tabs: { id: TabType; label: string; icon: React.ReactNode; badgeGreen?: number; badgeYellow?: number }[] = [
    { id: 'profile', label: 'My Profile', icon: <User className="w-4 h-4" /> },
    { id: 'members', label: 'Members', icon: <Users className="w-4 h-4" />, badgeGreen: activeCount || undefined, badgeYellow: nonActiveCount || undefined },
    ...(isAdmin ? [{ id: 'access' as TabType, label: 'Invites & Access', icon: <UserPlus className="w-4 h-4" />, badgeYellow: pendingCount || undefined }] : []),
    { id: 'appSettings', label: 'App Settings', icon: <SettingsIcon className="w-4 h-4" /> },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in-up">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Settings</h2>
        <p className="text-slate-500">{organization.name}</p>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all relative
              ${activeTab === tab.id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'}`}
          >
            {tab.icon}
            {tab.label}
            {tab.badgeGreen ? (
              <span className="bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none min-w-[18px] text-center">
                {tab.badgeGreen}
              </span>
            ) : null}
            {tab.badgeYellow ? (
              <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none min-w-[18px] text-center">
                {tab.badgeYellow}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* ── My Profile ─────────────────────────────────────────────────── */}
      {activeTab === 'profile' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" /> My Profile
            </h3>

            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-2xl font-bold text-white shrink-0">
                {nameValue.charAt(0).toUpperCase()}
              </div>

              <div className="flex-1 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Display Name</label>
                  {editingName ? (
                    <div className="flex gap-2">
                      <input
                        autoFocus
                        type="text"
                        value={nameValue}
                        onChange={e => setNameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setNameValue(currentUser.fullName); } }}
                        className="flex-1 p-2 border border-blue-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-slate-900 font-medium"
                      />
                      <button onClick={handleSaveName} disabled={savingName}
                        className="bg-blue-600 text-white px-3 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                        {savingName ? '...' : 'Save'}
                      </button>
                      <button onClick={() => { setEditingName(false); setNameValue(currentUser.fullName); }}
                        className="p-2 text-slate-400 hover:text-slate-600 rounded-lg border border-slate-200">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="text-slate-900 font-semibold text-lg">{nameValue}</span>
                      <button onClick={() => setEditingName(true)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium border border-blue-200 px-2 py-0.5 rounded">
                        Edit
                      </button>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email</label>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{currentUser.email}</span>
                  </div>
                </div>

                {/* Role + Admin status */}
                <div className="flex flex-wrap gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Role</label>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${roleColor(organization.role || '')}`}>
                      <Shield className="w-3 h-3" />
                      {ROLE_LABELS[organization.role || ''] || organization.role || 'Member'}
                    </span>
                  </div>
                  {isAdmin && (
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Access</label>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-amber-100 text-amber-700">
                        <ShieldCheck className="w-3 h-3" /> Admin
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Org info */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-slate-900 mb-4">Organization</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 text-white font-bold">
                {organization.name.charAt(0)}
              </div>
              <div>
                <div className="font-semibold text-slate-900">{organization.name}</div>
                <div className="text-xs text-slate-500">Join code: <span className="font-mono font-bold">{organization.joinCode}</span></div>
              </div>
            </div>
          </div>

          {/* Danger zone */}
          {organization.id !== 'dev-org-id' && (
            <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
              <h3 className="font-bold text-red-700 mb-1 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Danger Zone
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Permanently delete your account and all associated data. This cannot be undone.
              </p>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 border border-red-300 text-red-700 text-sm font-semibold rounded-xl hover:bg-red-50 transition-colors"
              >
                Delete My Account
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Members ────────────────────────────────────────────────────── */}
      {activeTab === 'members' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Team Members ({totalCount})
              {pendingCount > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
                  {pendingCount} pending
                </span>
              )}
            </h3>
            {loadingMembers && <div className="text-sm text-slate-400">Loading...</div>}
          </div>

          {memberError && (
            <div className="p-4 bg-red-50 text-red-600 text-sm border-b border-red-100">{memberError}</div>
          )}

          <div className="divide-y divide-slate-100">
            {members.length === 0 && !loadingMembers && (
              <div className="p-8 text-center text-slate-400 text-sm">No members yet.</div>
            )}

            {members.map(member => {
              const isSelf = member.userId === currentUser.id;
              const isPending = member.status === 'pending';
              const isInvited = member.status === 'invited';
              // Always use the live profile data for the current user — the DB may have a stale or missing full_name
              const displayName = isSelf ? currentUser.fullName : member.fullName;
              const displayEmail = isSelf ? currentUser.email : member.email;

              return (
                <div key={member.memberId}
                  className={`p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:bg-slate-50 transition-colors
                    ${isPending ? 'bg-amber-50/50' : ''}
                    ${isInvited ? 'bg-slate-50/50' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border shrink-0
                      ${isPending ? 'bg-amber-100 border-amber-200 text-amber-700' :
                        isInvited ? 'bg-slate-200 border-slate-300 text-slate-500' :
                        'bg-slate-100 border-slate-200 text-slate-700'}`}>
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                        {displayName}
                        {isSelf && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">You</span>}
                        {member.isAdmin && !isSelf && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" />Admin</span>}
                        {isSelf && isAdmin && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" />Admin</span>}
                        {isPending && <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />Pending</span>}
                        {isInvited && <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">Invited</span>}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        {displayEmail ? (
                          <><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{displayEmail}</span></>
                        ) : isPending ? (
                          <span className="text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Requesting: <strong>{ROLE_LABELS[member.requestedRole || ''] || member.requestedRole}</strong>
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pl-13 md:pl-0 flex-wrap">
                    {/* Pending approval actions */}
                    {isPending && isAdmin ? (
                      <>
                        <span className="text-xs text-slate-500 hidden md:block">
                          Wants: <strong>{ROLE_LABELS[member.requestedRole || ''] || member.requestedRole}</strong>
                        </span>
                        <button onClick={() => handleApprove(member)}
                          className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-700 transition-colors">
                          Approve
                        </button>
                        <button onClick={() => handleReject(member)}
                          className="bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 transition-colors">
                          Reject
                        </button>
                      </>
                    ) : isPending ? (
                      <span className="text-xs text-amber-600 font-medium">Awaiting approval</span>
                    ) : (
                      <>
                        {/* Role selector: admins can edit any role including their own; non-admins see a static badge */}
                        {isAdmin ? (
                          <div className="relative">
                            <select
                              value={member.role}
                              onChange={e => handleRoleChange(member.memberId, e.target.value)}
                              className="appearance-none bg-white border border-slate-300 text-slate-700 py-1.5 pl-3 pr-8 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                              {ASSIGNABLE_ROLES.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                            <UserCog className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
                          </div>
                        ) : (
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide flex items-center gap-1 ${roleColor(member.role)}`}>
                            <Shield className="w-3 h-3" />
                            {ROLE_LABELS[member.role] || member.role}
                          </span>
                        )}

                        {/* Admin toggle (admin can toggle for all active/invited non-self members) */}
                        {isAdmin && !isSelf && !isPending && (
                          <button
                            onClick={() => member.status === 'active' ? handleAdminToggle(member.memberId, !member.isAdmin) : undefined}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors
                              ${member.status !== 'active' ? 'opacity-40 cursor-not-allowed bg-slate-50 text-slate-400 border-slate-200' :
                                member.isAdmin
                                  ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                                  : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}
                            title={member.status !== 'active' ? 'Admin can be set once member accepts invite' : member.isAdmin ? 'Remove admin' : 'Make admin'}
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {member.isAdmin ? 'Admin' : 'Admin?'}
                          </button>
                        )}

                        {/* Remove / cancel invite (admin only, not self) */}
                        {isAdmin && !isSelf && (
                          <button
                            onClick={() => handleRemoveMember(member.memberId, displayName)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                            title={isInvited ? 'Cancel invite' : 'Remove member'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}

                        {/* GDPR delete account (admin only, active members with userId, not self) */}
                        {isAdmin && !isSelf && member.status === 'active' && member.userId && (
                          <button
                            onClick={() => { setDeletionTarget(member); setDeletionStep(1); }}
                            className="p-1.5 text-slate-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-300"
                            title="Request account deletion"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Invites & Access (Admin only) ───────────────────────────────── */}
      {activeTab === 'access' && isAdmin && (
        <div className="space-y-6">
          {/* QR Code + Join Code */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-600" /> Join Code & QR
            </h3>

            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* QR Code */}
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white p-3 rounded-xl border-2 border-slate-200 shadow-sm">
                  <QRCodeSVG value={joinUrl} size={160} level="M" />
                </div>
                <button onClick={handleCopyJoinUrl}
                  className="text-xs flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium">
                  {qrCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {qrCopied ? 'Link Copied!' : 'Copy Join Link'}
                </button>
              </div>

              {/* Join code */}
              <div className="flex-1">
                <p className="text-sm text-slate-500 mb-3">
                  Share the QR code or 6-character code to let coaches, staff, and parents join.
                  Coach role requests require admin approval.
                </p>
                <div className="bg-slate-100 p-3 rounded-xl flex items-center gap-3 border border-slate-200 mb-3 w-fit">
                  <div className="font-mono text-2xl font-bold tracking-[0.3em] text-slate-800 px-2">
                    {organization.joinCode}
                  </div>
                  <button onClick={handleCopyCode}
                    className="bg-white hover:bg-slate-50 text-slate-600 p-2 rounded-lg border border-slate-200 transition-colors"
                    title="Copy Code">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-400">
                  New users will choose their role when joining. Coach roles require your approval.
                </p>
              </div>
            </div>
          </div>

          {/* Send Invite */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-lg text-slate-900 mb-2 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" /> Send Invite
            </h3>
            <p className="text-sm text-slate-500 mb-5">
              Pre-register a member with a role. They'll appear as "invited" until they join using the code.
            </p>

            <form onSubmit={handleSendInvite} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Name</label>
                  <input
                    type="text" required
                    placeholder="Alex Johnson"
                    value={inviteName} onChange={e => setInviteName(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Email</label>
                  <input
                    type="email" required
                    placeholder="alex@example.com"
                    value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Role</label>
                  <select
                    value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                  >
                    {ASSIGNABLE_ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Admin Access</label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input type="checkbox" className="sr-only peer"
                        checked={inviteAdmin} onChange={e => setInviteAdmin(e.target.checked)} />
                      <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-amber-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </div>
                    <span className="text-sm text-slate-700 font-medium">
                      {inviteAdmin ? 'Yes – Admin' : 'No – Regular user'}
                    </span>
                  </label>
                </div>
              </div>

              {inviteError && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">{inviteError}</div>
              )}
              {inviteSuccess && (
                <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm border border-green-100 flex items-center gap-2">
                  <Check className="w-4 h-4" /> Invite created. Share the join code <strong>{organization.joinCode}</strong> with them.
                </div>
              )}

              <button type="submit" disabled={sendingInvite}
                className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-60">
                <Send className="w-4 h-4" />
                {sendingInvite ? 'Creating...' : 'Create Invite'}
              </button>
            </form>
          </div>

          {/* Pending Requests */}
          {members.filter(m => m.status === 'pending').length > 0 && (
            <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-amber-100 bg-amber-50">
                <h3 className="font-bold text-amber-900 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pending Coach Requests ({members.filter(m => m.status === 'pending').length})
                </h3>
                <p className="text-xs text-amber-700 mt-0.5">These users are waiting for approval to join as a coach.</p>
              </div>
              <div className="divide-y divide-amber-100">
                {members.filter(m => m.status === 'pending').map(member => (
                  <div key={member.memberId} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">{member.fullName}</div>
                      <div className="text-sm text-slate-500">
                        Requesting: <strong>{ROLE_LABELS[member.requestedRole || ''] || member.requestedRole}</strong>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleApprove(member)}
                        className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-sm font-bold hover:bg-green-700">
                        Approve
                      </button>
                      <button onClick={() => handleReject(member)}
                        className="bg-white text-red-600 border border-red-200 px-4 py-1.5 rounded-lg text-sm font-bold hover:bg-red-50">
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── App Settings ────────────────────────────────────────────────── */}
      {activeTab === 'appSettings' && (
        <div className="space-y-4">
          {/* Match Configuration */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                <SettingsIcon className="w-5 h-5 text-blue-600" /> Match Configuration
              </h3>
            </div>
            <div className="p-5 space-y-5">
              {/* Substitution Limit */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="font-bold text-slate-700 mb-1 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-slate-400" /> Substitution Limit (Per Set)
                  </div>
                  <p className="text-sm text-slate-500 max-w-sm">
                    Maximum substitutions per team in a single set. Standard: 12 (USAV) or 15 (NCAA).
                  </p>
                </div>
                <input
                  type="number" min="0" max="99"
                  className="w-24 p-2 border border-slate-300 rounded-lg text-center font-bold bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={appSettings.substitutionsPerSet}
                  onChange={e => handleSettingChange('substitutionsPerSet', parseInt(e.target.value) || 12)}
                />
              </div>

              {/* Two Liberos */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-slate-100 pt-5">
                <div>
                  <div className="font-bold text-slate-700 mb-1 flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" /> Two Liberos
                  </div>
                  <p className="text-sm text-slate-500 max-w-sm">
                    Enable International/USAV rule allowing two designated liberos per set.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input type="checkbox" className="sr-only peer"
                    checked={appSettings.allowTwoLiberos || false}
                    onChange={e => handleSettingChange('allowTwoLiberos', e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Stat Tracking — collapsible */}
              <div className="border-t border-slate-100 pt-5">
                <button
                  type="button"
                  onClick={() => setShowStatConfig(v => !v)}
                  className="w-full flex items-center justify-between gap-3 text-left hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-center gap-2 font-bold text-slate-700">
                    <BarChart2 className="w-4 h-4 text-slate-400" /> Stat Tracking
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${appSettings.simpleStats ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                      {appSettings.simpleStats ? 'Simple' : 'Advanced'}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${showStatConfig ? 'rotate-180' : ''}`} />
                </button>
                {showStatConfig && (() => {
                  const toggles: AdvancedStatToggles = { ...DEFAULT_ADVANCED_STAT_TOGGLES, ...appSettings.advancedStatToggles };
                  const handleToggle = (key: keyof AdvancedStatToggles) => {
                    setAppSettings(prev => ({
                      ...prev,
                      advancedStatToggles: { ...DEFAULT_ADVANCED_STAT_TOGGLES, ...prev.advancedStatToggles, [key]: !toggles[key] },
                    }));
                  };
                  const items: { key: keyof AdvancedStatToggles; label: string; description: string }[] = [
                    { key: 'serveTypeTracking', label: 'Serve Type Tracking', description: 'Track Float vs Topspin on serves — enables serve type selector and tags aces, errors, and attempts.' },
                    { key: 'killHitTypeRow', label: 'Kill Hit Type', description: 'Show Driven / Shot / Tip overlay row after recording a kill.' },
                    { key: 'killOutcomeRow', label: 'Kill Outcome', description: 'Show Tool / Floor / Shank overlay row after recording a kill.' },
                    { key: 'errorDetailOverlay', label: 'Error Detail Overlay', description: 'Show error type picker on attack errors instead of recording immediately.' },
                    { key: 'serveReceiveDetail', label: 'Serve-Receive Detail', description: 'Capture incoming serve style and contact type (hands/platform) on receptions.' },
                  ];
                  return (
                    <div className="mt-4 space-y-4">
                      {/* Simple/Advanced toggle */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div>
                          <p className="text-sm text-slate-500 max-w-sm">
                            <strong>Simple Stats</strong> speeds up live tracking by skipping detail prompts. <strong>Advanced Stats</strong> captures extra detail — configure which features are active below.
                          </p>
                        </div>
                        <button
                          onClick={() => handleSettingChange('simpleStats', !appSettings.simpleStats)}
                          className={`shrink-0 px-4 py-2 rounded-lg border text-sm font-bold transition-colors ${appSettings.simpleStats ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-orange-50 border-orange-300 text-orange-700'}`}
                        >
                          {appSettings.simpleStats ? 'Simple Stats' : 'Advanced Stats'}
                        </button>
                      </div>
                      {/* Advanced toggles */}
                      {!appSettings.simpleStats && (
                        <div className="ml-4 space-y-3 border-l-2 border-orange-200 pl-4">
                          {items.map(({ key, label, description }) => (
                            <div key={key} className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-slate-700">{label}</div>
                                <p className="text-xs text-slate-500 max-w-sm">{description}</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                                <input type="checkbox" className="sr-only peer"
                                  checked={toggles[key]}
                                  onChange={() => handleToggle(key)} />
                                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500"></div>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Default Opponent Serve: Float */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-slate-100 pt-5">
                <div>
                  <div className="font-bold text-slate-700 mb-1 flex items-center gap-2">
                    <Wind className="w-4 h-4 text-slate-400" /> Default Opponent Serve: Float
                  </div>
                  <p className="text-sm text-slate-500 max-w-sm">
                    When enabled, the incoming serve type automatically defaults to Float each time the opponent serves. You can still change it to Topspin during the game.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input type="checkbox" className="sr-only peer"
                    checked={appSettings.defaultOpponentServesFloat || false}
                    onChange={e => handleSettingChange('defaultOpponentServesFloat', e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Units */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                <Ruler className="w-5 h-5 text-purple-600" /> Units & Measurements
              </h3>
            </div>
            <div className="p-5 space-y-5">
              {/* Org-wide default — admin only */}
              {isAdmin && (
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 pb-5 border-b border-slate-100">
                  <div>
                    <div className="font-bold text-slate-700 mb-1">Organization Default</div>
                    <p className="text-sm text-slate-500 max-w-sm">
                      Sets the default unit system for all members of this organization. Members can override this for their own device in their personal settings.
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="flex bg-slate-100 p-1 rounded-lg">
                      <button
                        type="button"
                        onClick={async () => {
                          await supabase.from('organizations').update({ unit_preference: 'imperial' }).eq('id', organization.id);
                          onUpdateOrg?.({ unitPreference: 'imperial' });
                        }}
                        className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${(organization.unitPreference ?? 'imperial') === 'imperial' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                        Imperial (ft/in)
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          await supabase.from('organizations').update({ unit_preference: 'metric' }).eq('id', organization.id);
                          onUpdateOrg?.({ unitPreference: 'metric' });
                        }}
                        className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${organization.unitPreference === 'metric' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                        Metric (cm/m)
                      </button>
                    </div>
                    <p className="text-xs text-slate-400">Changes take effect immediately for new sessions.</p>
                  </div>
                </div>
              )}
              {/* Personal override */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="font-bold text-slate-700 mb-1">My Device</div>
                  <p className="text-sm text-slate-500 max-w-sm">Overrides the organization default on this device only.</p>
                </div>
                <div className="flex bg-slate-100 p-1 rounded-lg">
                  <button onClick={() => handleSettingChange('measurementSystem', 'imperial')}
                    className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${appSettings.measurementSystem === 'imperial' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    Imperial (ft/in)
                  </button>
                  <button onClick={() => handleSettingChange('measurementSystem', 'metric')}
                    className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${appSettings.measurementSystem === 'metric' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    Metric (cm/m)
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* AI Practice Insights */}
          {(() => {
            const insightStats = { ...DEFAULT_INSIGHT_STATS, ...appSettings.insightStats };
            const toggleInsight = (key: string, value: boolean) => {
              setAppSettings(prev => ({
                ...prev,
                insightStats: { ...DEFAULT_INSIGHT_STATS, ...prev.insightStats, [key]: value },
              }));
            };
            const activeCount = Object.values(insightStats).filter(Boolean).length;
            return (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-slate-100">
                  <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-600" /> AI Practice Insights
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Choose which stat categories Gemini considers when generating practice recommendations. Disable any category your team doesn't reliably track.
                  </p>
                </div>
                <div className="divide-y divide-slate-100">
                  {INSIGHT_CATEGORIES.map(cat => {
                    const enabled = (insightStats as Record<string, boolean>)[cat.id] ?? true;
                    return (
                      <div key={cat.id} className="flex items-center justify-between gap-4 px-5 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xl shrink-0">{cat.icon}</span>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-800 text-sm">{cat.label}</div>
                            <div className="text-xs text-slate-500">{cat.description}</div>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input type="checkbox" className="sr-only peer"
                            checked={enabled}
                            onChange={e => toggleInsight(cat.id, e.target.checked)} />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600" />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-100">
                  <p className="text-xs text-slate-400">
                    {activeCount} of {INSIGHT_CATEGORIES.length} categories active · Changes apply the next time you analyze your team.
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Stat Color Thresholds */}
          {(() => {
            const thresholds: StatThresholds = { ...DEFAULT_STAT_THRESHOLDS, ...appSettings.statThresholds };

            // Detect which preset (if any) currently matches
            const activePreset = Object.entries(THRESHOLD_PRESETS).find(([, p]) =>
              (Object.keys(p.thresholds) as (keyof StatThresholds)[]).every(
                k => Math.abs((p.thresholds[k] as number) - (thresholds[k] as number)) < 0.0001
              )
            )?.[0] ?? 'custom';

            const applyPreset = (key: string) => {
              if (key === 'custom') return;
              const preset = THRESHOLD_PRESETS[key];
              if (preset) handleSettingChange('statThresholds', { ...preset.thresholds });
            };

            // Helper: update one threshold key
            const setThreshold = (key: keyof StatThresholds, raw: string) => {
              const val = parseFloat(raw);
              if (isNaN(val)) return;
              handleSettingChange('statThresholds', { ...thresholds, [key]: val });
            };

            // Percent-type stats (stored 0–1, edited as 0–100 integers)
            const PctInput = ({ label, greenKey, amberKey, description }: {
              label: string; greenKey: keyof StatThresholds; amberKey: keyof StatThresholds; description: string;
            }) => {
              const g = Math.round((thresholds[greenKey] as number) * 100);
              const a = Math.round((thresholds[amberKey] as number) * 100);
              const invalid = g <= a;
              return (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start py-4 border-t border-slate-100 first:border-0 first:pt-0">
                  <div>
                    <div className="font-semibold text-slate-700 text-sm">{label}</div>
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{description}</p>
                    {invalid && <p className="text-xs text-red-500 mt-1">⚠ Green must be higher than amber.</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-green-600 uppercase">Green</span>
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" max="100" step="1"
                          className={`w-16 p-1.5 border rounded-lg text-center text-sm font-bold bg-green-50 outline-none focus:ring-2 ${invalid ? 'border-red-300 focus:ring-red-400' : 'border-green-300 focus:ring-green-400'}`}
                          value={g}
                          onChange={e => setThreshold(greenKey, String(parseInt(e.target.value) / 100))}
                        />
                        <span className="text-xs text-slate-400">%</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-amber-600 uppercase">Amber</span>
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" max="100" step="1"
                          className="w-16 p-1.5 border border-amber-300 rounded-lg text-center text-sm font-bold bg-amber-50 outline-none focus:ring-2 focus:ring-amber-400"
                          value={a}
                          onChange={e => setThreshold(amberKey, String(parseInt(e.target.value) / 100))}
                        />
                        <span className="text-xs text-slate-400">%</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1 opacity-50">
                      <span className="text-[10px] font-bold text-red-500 uppercase">Red</span>
                      <div className="w-16 p-1.5 border border-red-200 rounded-lg text-center text-sm font-bold bg-red-50 text-red-400">
                        &lt;{a}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            };

            // Raw-value stats (pass rating 0–3, ace/error ratio)
            const RawInput = ({ label, greenKey, amberKey, description, step, unit }: {
              label: string; greenKey: keyof StatThresholds; amberKey: keyof StatThresholds;
              description: string; step: number; unit?: string;
            }) => {
              const g = thresholds[greenKey] as number;
              const a = thresholds[amberKey] as number;
              const invalid = g <= a;
              return (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start py-4 border-t border-slate-100 first:border-0 first:pt-0">
                  <div>
                    <div className="font-semibold text-slate-700 text-sm">{label}</div>
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{description}</p>
                    {invalid && <p className="text-xs text-red-500 mt-1">⚠ Green must be higher than amber.</p>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-green-600 uppercase">Green</span>
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" step={step}
                          className={`w-16 p-1.5 border rounded-lg text-center text-sm font-bold bg-green-50 outline-none focus:ring-2 ${invalid ? 'border-red-300 focus:ring-red-400' : 'border-green-300 focus:ring-green-400'}`}
                          value={g.toFixed(step < 0.1 ? 2 : 1)}
                          onChange={e => setThreshold(greenKey, e.target.value)}
                        />
                        {unit && <span className="text-xs text-slate-400">{unit}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-amber-600 uppercase">Amber</span>
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" step={step}
                          className="w-16 p-1.5 border border-amber-300 rounded-lg text-center text-sm font-bold bg-amber-50 outline-none focus:ring-2 focus:ring-amber-400"
                          value={a.toFixed(step < 0.1 ? 2 : 1)}
                          onChange={e => setThreshold(amberKey, e.target.value)}
                        />
                        {unit && <span className="text-xs text-slate-400">{unit}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1 opacity-50">
                      <span className="text-[10px] font-bold text-red-500 uppercase">Red</span>
                      <div className="w-16 p-1.5 border border-red-200 rounded-lg text-center text-sm font-bold bg-red-50 text-red-400">
                        &lt;{a.toFixed(1)}{unit}
                      </div>
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowThresholds(v => !v)}
                  className="w-full p-5 flex items-start justify-between gap-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                      <Palette className="w-5 h-5 text-indigo-600" /> Stat Color Thresholds
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Control when stats show as <span className="text-green-600 font-semibold">green</span> (on target), <span className="text-amber-500 font-semibold">amber</span> (borderline), or <span className="text-red-500 font-semibold">red</span> (needs improvement). Select a level preset or set custom values.
                    </p>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 mt-1 transition-transform ${showThresholds ? 'rotate-180' : ''}`} />
                </button>

                {showThresholds && <div className="p-5 space-y-6 border-t border-slate-100">
                  {/* Preset selector */}
                  <div>
                    <div className="font-semibold text-slate-700 text-sm mb-2">Level Preset</div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {[...Object.entries(THRESHOLD_PRESETS), ['custom', { label: 'Custom', description: 'Your own values.' }] as const].map(([key, preset]) => (
                        <button
                          key={key}
                          onClick={() => applyPreset(key as string)}
                          className={`px-3 py-2.5 rounded-lg border text-xs font-bold transition-all text-left ${
                            activePreset === key
                              ? 'bg-indigo-600 border-indigo-700 text-white shadow-sm'
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-indigo-50'
                          }`}
                        >
                          <div>{(preset as any).label}</div>
                          {key !== 'custom' && <div className={`text-[10px] mt-0.5 font-normal ${activePreset === key ? 'text-indigo-200' : 'text-slate-400'}`}>{(preset as any).description}</div>}
                        </button>
                      ))}
                    </div>
                    {activePreset === 'custom' && (
                      <div className="flex items-start gap-2 mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-indigo-700">Custom values active. Select a preset to reset to level-based defaults.</p>
                      </div>
                    )}
                  </div>

                  {/* Macro metrics */}
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Team Macro Metrics</div>
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-0">
                      <PctInput
                        label="Sideout %"
                        greenKey="sideoutPctGreen" amberKey="sideoutPctAmber"
                        description="% of serve-receive rallies your team wins (side out). Tracked automatically during live GameTime sessions. Elite collegiate target is ~65%; varsity benchmark is 60%."
                      />
                      <PctInput
                        label="Break Point %"
                        greenKey="breakPointPctGreen" amberKey="breakPointPctAmber"
                        description="% of serve rallies your team wins (points scored while serving). Represents your ability to hold serve or 'break' the opponent's rhythm. Varsity target is ~35%."
                      />
                    </div>
                  </div>

                  {/* Micro metrics */}
                  <div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Team Micro Metrics</div>
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-0">
                      <RawInput
                        label="Pass Rating (Serve Receive Avg)"
                        greenKey="passRatingGreen" amberKey="passRatingAmber"
                        description="Weighted average quality of serve-receive passes on a 0–3 scale. 3 = perfect (no setter adjustment needed), 2 = good (options available), 1 = poor (limited options), 0 = ace/error. Varsity benchmark is ≥2.0."
                        step={0.1}
                      />
                      <RawInput
                        label="Hitting Efficiency (H%)"
                        greenKey="hitEfficiencyGreen" amberKey="hitEfficiencyAmber"
                        description="(Kills − Errors) ÷ Total Attacks. Accounts for both kills AND errors — a high kill % with many errors can still yield a low efficiency. Varsity: ≥0.25 (25%). College: ≥0.30."
                        step={0.01}
                      />
                      <PctInput
                        label="Serve % (In-Play)"
                        greenKey="servePctGreen" amberKey="servePctAmber"
                        description="(Attempts − Errors) ÷ Attempts. Percentage of serves that land in-play (no service error). Aggressive servers may score more aces but sacrifice serve %. Varsity target: ≥90%."
                      />
                      <PctInput
                        label="Kill %"
                        greenKey="killPctGreen" amberKey="killPctAmber"
                        description="Kills ÷ Total Attacks. Unlike Hitting Efficiency, this doesn't penalize errors — it only measures how often an attack results in a kill. Varsity target: ≥40%."
                      />
                      <RawInput
                        label="Aces / Errors Ratio"
                        greenKey="aceErrorRatioGreen" amberKey="aceErrorRatioAmber"
                        description="Aces ÷ Service Errors. A ratio above 1.0 means the team earns more free points than it gives away on serve. ≥1.0 is excellent; below 0.5 means errors are hurting more than aces help."
                        step={0.1}
                      />
                    </div>
                  </div>
                </div>}
              </div>
            );
          })()}

          {/* Archived Teams */}
          {(loadingArchived || archivedTeams.length > 0) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex items-center gap-2">
                <ArchiveRestore className="w-5 h-5 text-amber-600" />
                <h3 className="font-bold text-slate-900">Archived Teams</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {loadingArchived ? (
                  <div className="p-5 text-sm text-slate-400">Loading…</div>
                ) : archivedTeams.map(team => {
                  const isRestoring = restoringTeamId === team.id;
                  return (
                    <div key={team.id} className="p-4 flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{team.name}</p>
                        <p className="text-xs text-slate-400 capitalize">{team.type ?? 'club'} team</p>
                      </div>
                      <button
                        type="button"
                        disabled={isRestoring}
                        onClick={() => handleRestoreTeam(team)}
                        className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <ArchiveRestore className="w-4 h-4" />
                        {isRestoring ? 'Restoring…' : 'Restore'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Features Usage */}
          {(() => {
            const FEATURE_LABELS: Record<string, string> = {
              voice_logging:       'Voice Logging',
              game_suggestions:    'AI Game Suggestions',
              drills:              'Practice Drills',
              extract_stats:       'Stats Extraction',
              extract_schedule:    'Schedule Import',
              parse_lineup:        'Lineup Sheet Scanner',
              ai_practice_insights:'AI Practice Insights',
            };
            const totalTokens = aiUsage.reduce((s, f) => s + f.tokens, 0);
            const totalPct = Math.min(Math.round((totalTokens / aiMonthlyLimit) * 100), 100);
            const now = new Date();
            const monthLabel = now.toLocaleString('default', { month: 'long', year: 'numeric' });
            const barColor = totalPct >= 90 ? 'bg-red-500' : totalPct >= 75 ? 'bg-orange-400' : totalPct >= 50 ? 'bg-amber-400' : 'bg-blue-500';
            return (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-600" /> AI Features Usage
                  </h3>
                  <span className="text-xs text-slate-400">{monthLabel}</span>
                </div>
                <div className="p-5">
                  {aiUsageLoading ? (
                    <p className="text-sm text-slate-400 text-center py-4">Loading…</p>
                  ) : aiUsage.length === 0 && totalTokens === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No AI features used this month.</p>
                  ) : (
                    <div className="space-y-4">
                      {/* Overall bar */}
                      <div>
                        <div className="flex justify-between items-baseline mb-1.5">
                          <span className="text-sm font-semibold text-slate-700">Total this month</span>
                          <span className={`text-sm font-bold ${totalPct >= 90 ? 'text-red-600' : totalPct >= 75 ? 'text-orange-500' : totalPct >= 50 ? 'text-amber-600' : 'text-slate-700'}`}>
                            {totalPct}% of limit
                          </span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${totalPct}%` }} />
                        </div>
                        {totalPct >= 50 && (
                          <p className="text-xs text-slate-400 mt-1.5">
                            {totalPct >= 90 ? 'Almost at the limit — AI features may stop working before month end.' :
                             totalPct >= 75 ? 'Running low — consider reducing AI feature usage.' :
                             'Halfway through the monthly limit.'}
                          </p>
                        )}
                      </div>
                      {/* Per-feature breakdown */}
                      {aiUsage.length > 0 && (
                        <div className="space-y-2.5 border-t border-slate-100 pt-4">
                          {aiUsage.map(({ feature, tokens }) => {
                            const pct = Math.min(Math.round((tokens / aiMonthlyLimit) * 100), 100);
                            const label = FEATURE_LABELS[feature] ?? feature.replace(/_/g, ' ');
                            return (
                              <div key={feature}>
                                <div className="flex justify-between items-baseline mb-1">
                                  <span className="text-xs font-medium text-slate-600">{label}</span>
                                  <span className="text-xs text-slate-400">{pct}%</span>
                                </div>
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {onRestartTour && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex items-center justify-between gap-4">
              <div>
                <p className="font-bold text-slate-800 text-sm">Restart Onboarding Tour</p>
                <p className="text-xs text-slate-500 mt-0.5">Show the welcome tour and section tips again from the beginning.</p>
              </div>
              <button
                type="button"
                onClick={onRestartTour}
                className="shrink-0 px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Restart Tour
              </button>
            </div>
          )}

          <p className="text-center text-slate-400 text-xs pb-2">Settings are saved automatically to this device.</p>
        </div>
      )}

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => setShowDeleteModal(false)}
        />
      )}

      {/* ── GDPR deletion success toast ── */}
      {deletionSuccessMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 max-w-sm text-center">
          <Check className="w-4 h-4 shrink-0" />
          {deletionSuccessMsg}
        </div>
      )}

      {/* ── GDPR admin deletion modal ── */}
      {deletionTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => { if (!requestingDeletion) { setDeletionTarget(null); setDeletionStep(1); } }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            {deletionStep === 1 ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <UserX className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">Request Account Deletion</h3>
                    <p className="text-xs text-slate-500">Admin action — cannot be undone after 48 hours</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                  You are about to request deletion of <strong>{deletionTarget.fullName || deletionTarget.email}</strong>'s account. They will receive an email with a 48-hour window to cancel before all their data is permanently erased.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setDeletionTarget(null); setDeletionStep(1); }}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletionStep(2)}
                    className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">Final Confirmation</h3>
                    <p className="text-xs text-red-500 font-medium">This will schedule permanent data deletion</p>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
                  <p className="text-sm text-red-700 font-medium mb-1">What will happen:</p>
                  <ul className="text-xs text-red-600 space-y-1 list-disc list-inside">
                    <li>An email is sent to {deletionTarget.email} with a cancellation link</li>
                    <li>After 48 hours, their account and all associated data are permanently deleted</li>
                    <li>If they are the sole member of any org, that org's data is deleted too</li>
                  </ul>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setDeletionStep(1)}
                    disabled={requestingDeletion}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    Go Back
                  </button>
                  <button
                    type="button"
                    onClick={handleRequestDeletion}
                    disabled={requestingDeletion}
                    className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {requestingDeletion && <Loader2 className="w-4 h-4 animate-spin" />}
                    {requestingDeletion ? 'Sending...' : 'Send Deletion Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgSettings;
